import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import classnames from 'classnames';

import Button from '@components/Button';
import { DeleteModal } from '@components/ModalBase';

import { createAuditTrail } from '@actions/agency';
import { logoutRequest } from '@actions/auth';
import { fetchDelegateWorkConfig, fetchStaffTextProfile } from '@actions/user';

import { appURLs, getInstanceWatermark, getLandingPagePartialURL, isTablet } from '@utils/AppUtil';
import {
  authService,
  getCurrentAgency,
  getUserProfile,
  hasActionCenterAccess,
  hasCEExecutiveReportsAccess,
  hasCRMExecutiveReportsAccess,
  hasInternalSubmissionAccess,
  hasProductCEAccess,
  hasProductCRMAccess,
  hasReportAccess,
  userLogOut,
} from '@utils/AuthUtil';
import { getFormattedDeviceDetails } from '@utils/DeviceDetailsUtil';
import { getLabels } from '@utils/MixPanelLabels';
import { getStaffMixpanelAgencyProps, getStaffTextPromoProps, track } from '@utils/MixPanelUtils';

import DelegateWorkModal from '../DelegateWorkModal';
import { STAFF_TEXT_SECTION_ID } from '../Profile/StaffText/staffTextUtil';

import CreateOptionsDropdown from './CreateOptionsDropdown';
import StaffTextCreatePromo from './StaffTextCreatePromo';
import {
  dismissStaffTextCreatePromo,
  isStaffTextCreatePromoDismissed,
  shouldShowStaffTextCreatePromo,
} from './staffTextPromo';
import ToolsDropdown, { ToolType } from './ToolsDropdown';
import UserProfileSection from './UserProfileSection';

import './style.scss';

interface IHeaderProps extends RouteComponentProps<{}> {
  // from redux
  agencyInfo: IAgencyInformationResponse;
  currenyAgency: IAgencyResponse;
  auth: IAuthStore;
  isFetchingProductLayout?: boolean;
  isCreateCaseConfigValid: boolean;
  productLayout: IProductLayoutResponse;
  entityIcons: IEntityIconResponse[];
  delegateWorkConfig: IDelegateWorkResponse;
  configViolationTypes: IConfigXerceViolationTypeResponse[];
  staffTextProfile: IStaffTextProfileResponse | null;
  logoutRequest: () => void;
  fetchDelegateWorkConfig: TFetchDelegateWorkConfig;
  fetchStaffTextProfile: TFetchStaffTextProfile;
  createAuditTrail: TAuditTrailCreateRequest;
  isFetchingAgencyInfo?: boolean;
}

interface IHeaderState {
  isToolsDropdownVisible: boolean;
  isCreateCaseErrorModalOpen: boolean;
  isCreateOptionsDropdownVisible: boolean;
  isDelegateWorkModalOpen: boolean;
  isStaffTextPromoDismissed: boolean;
}

export class Header extends React.Component<IHeaderProps, IHeaderState> {
  userProfile: IUserProfile;
  private hasTrackedStaffTextPromoShown = false;

  constructor(props: IHeaderProps) {
    super(props);

    this.userProfile = getUserProfile();
    this.state = this.getEmptyState();

    if (!this.props.auth.isSuperAdmin) {
      this.props.fetchDelegateWorkConfig();
    }
  }

  componentDidMount() {
    if (this.props.auth.isSuperAdmin || !this.props.fetchStaffTextProfile) {
      return;
    }
    if (!this.userProfile || !hasInternalSubmissionAccess(this.userProfile)) {
      return;
    }

    this.props.fetchStaffTextProfile();
  }

  componentDidUpdate() {
    this.trackStaffTextPromoShown();
  }

  getEmptyState = (): IHeaderState => ({
    isToolsDropdownVisible: false,
    isCreateCaseErrorModalOpen: false,
    isCreateOptionsDropdownVisible: false,
    isDelegateWorkModalOpen: false,
    isStaffTextPromoDismissed: isStaffTextCreatePromoDismissed(this.userProfile && this.userProfile.id),
  });

  toggleToolsDropdown = (e: React.SyntheticEvent<HTMLElement>) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();

    this.setState((prevState) => ({
      isCreateOptionsDropdownVisible: false,
      isToolsDropdownVisible: prevState.isCreateOptionsDropdownVisible || !prevState.isToolsDropdownVisible,
    }));
  };

  toggleCreateActionsDropdown = (e: React.SyntheticEvent<HTMLElement>) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();

    this.setState((prevState) => ({
      isToolsDropdownVisible: false,
      isCreateOptionsDropdownVisible: prevState.isToolsDropdownVisible || !prevState.isCreateOptionsDropdownVisible,
    }));
  };

  closeToolsDropdown = (e: React.SyntheticEvent<HTMLElement>) => {
    this.setState((prevState) => ({
      isToolsDropdownVisible: false,
    }));
  };

  closeCreateOptionsDropdown = (e: React.SyntheticEvent<HTMLElement>) => {
    this.setState((prevState) => ({
      isCreateOptionsDropdownVisible: false,
    }));
  };

  shouldShowStaffTextPromo = () =>
    !!this.userProfile &&
    hasInternalSubmissionAccess(this.userProfile) &&
    shouldShowStaffTextCreatePromo(this.props.staffTextProfile, this.state.isStaffTextPromoDismissed);

  getStaffTextPromoTrackProps = () => getStaffTextPromoProps();

  trackStaffTextPromoShown = () => {
    if (!this.state.isCreateOptionsDropdownVisible) {
      this.hasTrackedStaffTextPromoShown = false;

      return;
    }
    if (this.hasTrackedStaffTextPromoShown || !this.shouldShowStaffTextPromo()) {
      return;
    }

    this.hasTrackedStaffTextPromoShown = true;
    track(getLabels().TEXT_A_PHOTO_PROMO_SHOWN, this.getStaffTextPromoTrackProps());
  };

  goToStaffTextSection = (e: React.MouseEvent<HTMLAnchorElement>) => {
    track(getLabels().TEXT_A_PHOTO_PROMO_CLICKED, this.getStaffTextPromoTrackProps());

    const isModifiedClick = e.ctrlKey || e.shiftKey || e.metaKey || e.button || e.button === 1;
    if (!isModifiedClick) {
      e.preventDefault();
      const { history, auth } = this.props;
      history.push({
        ...this.props.location,
        pathname: `/agencies/${auth.userProfile.agencyId}/${appURLs.profilePartialURL}`,
        hash: `#${STAFF_TEXT_SECTION_ID}`,
        search: this.searchForHeaderTab(appURLs.profilePartialURL),
        state: this.props.location,
      });
    }

    this.closeCreateOptionsDropdown(e);
  };

  dismissStaffTextPromo = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dismissStaffTextCreatePromo(this.userProfile && this.userProfile.id);
    this.setState({ isStaffTextPromoDismissed: true });
    track(getLabels().TEXT_A_PHOTO_PROMO_DISMISSED, this.getStaffTextPromoTrackProps());
  };

  logout = async () => {
    const { history, auth } = this.props;
    const currentAgency = getCurrentAgency();

    await this.props.createAuditTrail({
      activityType: ActivityType.LOGOUT,
      componentType: AuditComponentType.LOGOUT,
    });
    userLogOut();

    if (currentAgency) {
      await authService.signOut();
    } else {
      history.push(`/agencies/${auth.userProfile.agencyId}/login`);
    }
  };

  redirectToAgencySetup = () => {
    const { history, auth } = this.props;

    history.push(`/agencies/${auth.userProfile.agencyId}/${appURLs.agencySetupPartialURL}`);
  };

  searchForHeaderTab = (url: string): string => {
    if (url === appURLs.actionCenterPartialURL) {
      return '';
    }

    const raw = this.props.location.search || '';
    if (!raw) {
      return '';
    }

    const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
    params.delete('phone_number');
    params.delete('lookup_id');
    params.delete('name');
    const next = params.toString();

    return next ? `?${next}` : '';
  };

  redirectTo = (url: string) => (e?: any) => {
    if (e.ctrlKey || e.shiftKey || e.metaKey || e.button || e.button === 1) {
      return;
    }
    e.preventDefault();

    const { history, auth } = this.props;
    const location = {
      ...this.props.location,
      pathname: `/agencies/${auth.userProfile.agencyId}/${url}`,
      search: this.searchForHeaderTab(url),
      state: this.props.location,
    };

    history.push(location);
  };

  isLinkSelected = (url: string) => {
    const currentPath = this.props.history.location.pathname;
    const { auth } = this.props;

    return currentPath.indexOf(`/agencies/${auth.userProfile.agencyId}/${url}`) !== -1;
  };

  toggleCreateCaseErrorModal = (e: React.SyntheticEvent<any>) => {
    this.setState((prevState) => ({
      isCreateCaseErrorModalOpen: !prevState.isCreateCaseErrorModalOpen,
    }));
  };

  validateCaseConfig = (e: any) => {
    const isModifiedClick = e.ctrlKey || e.shiftKey || e.metaKey || e.button || e.button === 1;
    track('CREATE_CASE_BUTTON_CLICK', {
      'Device Details': getFormattedDeviceDetails(),
    });
    if (!this.props.isCreateCaseConfigValid) {
      e.preventDefault();
      this.toggleCreateCaseErrorModal(e);

      return;
    }
    if (isModifiedClick) {
      return;
    }

    e.preventDefault();
    const { history, auth } = this.props;
    const url = `/agencies/${auth.userProfile.agencyId}/${appURLs.caseCreatePartialURL}`;
    const location = {
      ...this.props.location,
      pathname: url,
      state: this.props.location,
    };

    history.push(location);
  };

  redirectToCSP = (url: string) => (e?: any) => {
    if (e && (e.ctrlKey || e.shiftKey || e.metaKey || e.button || e.button === 1)) {
      return;
    }
    if (e) {
      e.preventDefault();
    }

    const { history } = this.props;
    const location = {
      ...this.props.location,
      pathname: url,
      state: this.props.location,
    };

    history.push(location);
  };

  renderUserProfileSection = () => {
    const { auth, delegateWorkConfig } = this.props;

    return (
      <UserProfileSection
        auth={auth}
        logout={this.logout}
        redirectTo={this.redirectTo}
        openDelegateWorkModal={this.toggleDelegateWorkModal}
        isWorkDelegationOn={delegateWorkConfig && delegateWorkConfig.isActive}
      />
    );
  };

  trackNavigationTabClick = (url: string) => {
    switch (url) {
      case appURLs.dashboardPartialURL:
        track('AGENCY_DASHBOARD_TAB', {
          'Device Details': getFormattedDeviceDetails(),
        });
        break;
      case appURLs.reportsPartialURL:
        track('AGENCY_REPORTS_TAB', {
          'Device Details': getFormattedDeviceDetails(),
        });
        break;
      case appURLs.casesPartialURL:
        track('AGENCY_CLP_TAB', {
          'Device Details': getFormattedDeviceDetails(),
        });
        break;
      case appURLs.customerSubmissionsPartialURL:
        track('AGENCY_CRM_TAB', {
          'Device Details': getFormattedDeviceDetails(),
          ...getStaffMixpanelAgencyProps(),
        });
        break;
      default:
        break;
    }
  };

  handleTabClick = (url: string) => (e?: React.SyntheticEvent<any>) => {
    if (!url) {
      e.preventDefault();

      return;
    }
    this.trackNavigationTabClick(url);
    this.redirectTo(url)(e);
  };

  renderLinks = () => {
    const { auth } = this.props;
    const hasCEReportsAccess = hasReportAccess(this.userProfile) || hasCEExecutiveReportsAccess(this.userProfile);
    const hasCRMExeutiveReportAccess = hasCRMExecutiveReportsAccess(this.userProfile);
    const isCEDeactivated = !this.userProfile.scopes.xerce || !this.userProfile.scopes.xerce.isActive;
    const linksArray = hasProductCEAccess(this.userProfile)
      ? [
          { path: appURLs.dashboardPartialURL, title: 'Dashboard' },
          ...(hasCEReportsAccess || hasCRMExeutiveReportAccess
            ? [{ path: appURLs.reportsPartialURL, title: 'Reports' }]
            : []),
          { path: appURLs.casesPartialURL, title: 'Cases' },
        ]
      : [{ path: appURLs.dashboardPartialURL, title: 'Dashboard' }];

    if (hasProductCRMAccess(this.userProfile)) {
      if (hasCRMExeutiveReportAccess && isCEDeactivated) {
        linksArray.push({ path: appURLs.reportsPartialURL, title: 'Reports' });
      }
      linksArray.push({
        path: appURLs.customerSubmissionsPartialURL,
        title: 'Customer Submissions',
      });
    }

    if (hasActionCenterAccess(this.userProfile) && hasInternalSubmissionAccess(this.userProfile) && !isTablet()) {
      linksArray.push({
        path: appURLs.actionCenterPartialURL,
        title: 'Action Center',
      });
    }

    return (
      <div className={classnames('app-header__links-container')}>
        {linksArray.map((link, index) => (
          <a
            href={`/agencies/${auth.userProfile.agencyId}/${link.path}`}
            onClick={this.handleTabClick(link.path)}
            key={index}
            className={classnames('app-header__link', {
              'app-header__link--active': this.isLinkSelected(link.path),
            })}
          >
            {link.title}
          </a>
        ))}
      </div>
    );
  };

  renderHeaderLeft = () => {
    const { currenyAgency } = this.props;
    const userProfile = getUserProfile();

    const agencyLogo = currenyAgency ? currenyAgency.logoUrl : '';
    const logoLink = `${userProfile.agencyId}/${getLandingPagePartialURL(userProfile)}`;

    return (
      <div className={classnames('app-header__left')}>
        {agencyLogo ? (
          <a href={`/agencies/${logoLink}`}>
            <img alt="" src={agencyLogo} className={classnames('agency-header__logo')} />
          </a>
        ) : (
          <div className={classnames('agency-header__empty-logo')} />
        )}
        {this.renderLinks()}
      </div>
    );
  };

  openPdfInNewTab = () => {
    const helpDocUrl = process.env.HELP_DOC_URL;
    const agencyWindow = window.open(helpDocUrl, '_blank');
    agencyWindow.focus();
  };

  renderHeaderRight = () => {
    /* TODO - To be used in future
    const productAccessTools = [
      {
        toolType: ToolType.PRINT_QUEUE,
        onClick: () => null,
      },
      {
        toolType: ToolType.FEES,
        onClick: () => null,
      },
      {
        toolType: ToolType.CONTACTS,
        onClick: () => null,
      },
    ];
    */

    const { auth, productLayout } = this.props;
    const agencySetupTool = {
      toolType: ToolType.AGENCY_SETUP,
      onClick: this.redirectTo(appURLs.agencySetupPartialURL),
      // tslint:disable-next-line: max-line-length
      URL: `/agencies/${auth.userProfile.agencyId}/${appURLs.agencySetupPartialURL}`,
    };

    const helpTool = {
      toolType: ToolType.HELP,
      onClick: this.openPdfInNewTab,
    };
    const contactsTool = {
      toolType: ToolType.CONTACTS,
      onClick: this.redirectTo(appURLs.contactsPartialURL),
      // tslint:disable-next-line: max-line-length
      URL: `/agencies/${auth.userProfile.agencyId}/${appURLs.contactsPartialURL}`,
    };
    const commlinkTool = {
      toolType: ToolType.COMMLINK,
      onClick: this.redirectTo(appURLs.commlinkPartialURL),
      // tslint:disable-next-line: max-line-length
      URL: `/agencies/${auth.userProfile.agencyId}/${appURLs.commlinkPartialURL}`,
    };

    const transactionsTool = {
      toolType: ToolType.FINANCE,
      onClick: this.redirectTo(appURLs.financePartialURL),
      // tslint:disable-next-line: max-line-length
      URL: `/agencies/${auth.userProfile.agencyId}/${appURLs.financePartialURL}`,
    };

    const tools = [];
    /* TODO - To be used in future
     if (this.props.auth.hasProductCEAccess) {
       tools = [...productAccessTools];
     }
    */
    if (
      this.props.auth.hasProductCEAdminAccess ||
      this.props.auth.hasProductCRMAdminAccess ||
      this.props.auth.hasSiteAdminAccess ||
      this.props.auth.hasUserAdminAccess
    ) {
      tools.push(agencySetupTool);
    }
    tools.push(helpTool);

    if (!!this.props.entityIcons && !!productLayout && !!productLayout.xerce) {
      const { entitySections } = productLayout.xerce;
      const entitySectionToViolationType = entitySections.reduce((acc, entitySection) => {
        acc[entitySection.id] = entitySection.configXerceViolationType.id;

        return acc;
      }, {});

      const onEntityIconClick = (entityIcon: IEntityIconResponse, e: any) => {
        track(`${entityIcon.label}_ICON_CLICKED`, {
          'Device Details': getFormattedDeviceDetails(),
        });
        this.redirectTo(`${appURLs.entitiesPartialURL}/${entitySectionToViolationType[entityIcon.id]}`)(e);
      };
      this.props.entityIcons.forEach((entityIcon) => {
        const entityLink = {
          onClick: (e: any) => onEntityIconClick(entityIcon, e),
          URL: `/agencies/${auth.userProfile.agencyId}/${appURLs.entitiesPartialURL}/${
            entitySectionToViolationType[entityIcon.id]
          }`,
          iconUrl: entityIcon.iconUrl,
          label: entityIcon.label,
        };
        tools.push({ ...entityLink });
      });
    }

    if (this.props.auth.hasProductCEAccess || this.props.auth.hasProductCRMAccess) {
      tools.push(contactsTool);
    }

    if (this.props.auth.hasProductCRMAccess && productLayout?.crm?.isMobileAppEnabled) {
      tools.push(commlinkTool);
    }

    if (this.props.auth.hasProductCEAccess && getUserProfile().allowFineTracking) {
      tools.push(transactionsTool);
    }

    const createOptions = [];

    if (this.props.auth.hasCreateCasePageAccess) {
      const caseUrl = `/agencies/${auth.userProfile.agencyId}/${appURLs.caseCreatePartialURL}`;
      createOptions.push({
        onClick: this.validateCaseConfig,
        URL: caseUrl,
        label: 'Code Enforcement Case',
      });
    }

    if (this.props.auth.hasInternalSubmisssionAccess) {
      const submissionUrl = `/agencies/${auth.userProfile.agencyId}/${appURLs.createCustomerSubmissionPartialURL}`;
      createOptions.push({
        onClick: this.redirectToCSP(submissionUrl),
        URL: submissionUrl,
        label: 'Customer Submission',
      });
    }

    const showStaffTextPromo = this.shouldShowStaffTextPromo();
    // One create action is normally a direct link. Eligible staff still need the menu so the promo can sit in it.
    const showCreateMenu = createOptions.length > 1 || (showStaffTextPromo && createOptions.length === 1);
    const staffTextPromo = showStaffTextPromo ? (
      <StaffTextCreatePromo
        href={`/agencies/${auth.userProfile.agencyId}/${appURLs.profilePartialURL}#${STAFF_TEXT_SECTION_ID}`}
        onNavigate={this.goToStaffTextSection}
        onDismiss={this.dismissStaffTextPromo}
      />
    ) : null;

    return (
      <div className={classnames('app-header__right')}>
        {/* TODO - To be used in future
        this.props.auth.hasProductCEAccess && (
          <div className={classnames('app-header__search')} />
        ) */}
        <div className="instance-watermark">{getInstanceWatermark()}</div>
        {showCreateMenu ? (
          <div className={classnames('app-header__menu', 'flex-row--center')}>
            <div className={classnames('app-header__new')} onClick={this.toggleCreateActionsDropdown} />
            <CreateOptionsDropdown
              pathName={this.props.location.pathname}
              show={this.state.isCreateOptionsDropdownVisible}
              onClose={this.closeCreateOptionsDropdown}
              options={createOptions}
              promo={staffTextPromo}
            />
          </div>
        ) : (
          createOptions.length === 1 && <a className={classnames('app-header__new')} {...createOptions[0]} />
        )}
        {createOptions.length && <div className={classnames('veritcal-pipe')} />}
        {/* <div className={classnames('app-header__notification')} /> */}
        <div className={classnames('app-header__menu', 'flex-row--center')}>
          <div className={classnames('app-header__menu__icon')} onClick={this.toggleToolsDropdown} />
          <ToolsDropdown
            pathName={this.props.location.pathname}
            show={this.state.isToolsDropdownVisible}
            onClose={this.closeToolsDropdown}
            tools={tools}
          />
        </div>
        {this.renderUserProfileSection()}
      </div>
    );
  };

  renderCreateCaseErrorModal = () => {
    const disclaimerText = `Requested action cannot be completed.
    One or more required components are missing or inactive.
    Please contact your administrator.`;

    const deleteModalFooter = (
      <div className={classnames('flex-row--middle')}>
        <Button onClick={this.toggleCreateCaseErrorModal} bsStyle={ButtonStyle.DANGER}>
          Okay
        </Button>
      </div>
    );

    return (
      <DeleteModal
        show={this.state.isCreateCaseErrorModalOpen}
        footer={deleteModalFooter}
        disclaimerText={disclaimerText}
        classNames={classnames('create-case-error-modal')}
      />
    );
  };

  toggleDelegateWorkModal = () => {
    this.setState((prevState) => ({
      isDelegateWorkModalOpen: !prevState.isDelegateWorkModalOpen,
    }));
  };

  render() {
    const { delegateWorkConfig, configViolationTypes } = this.props;
    const { isDelegateWorkModalOpen } = this.state;

    return (
      <div className={classnames('app-header')} id="header">
        {this.renderHeaderLeft()}
        {this.renderHeaderRight()}
        {this.renderCreateCaseErrorModal()}
        <DelegateWorkModal
          show={isDelegateWorkModalOpen}
          onClose={this.toggleDelegateWorkModal}
          delegateWorkConfig={delegateWorkConfig}
          configViolationTypes={configViolationTypes}
          isEdit={!!(delegateWorkConfig && delegateWorkConfig.id)}
        />
      </div>
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  agencyInfo: state.agency.agencyInfo,
  auth: state.auth,
  isCreateCaseConfigValid:
    state.productLayout.productLayout &&
    state.productLayout.productLayout.xerce &&
    state.productLayout.productLayout.xerce.isCreateCaseConfigValid,
  productLayout: state.productLayout.productLayout,
  currenyAgency: state.agency.currentAgency,
  entityIcons: state.entitySection.entityIcons,
  delegateWorkConfig: state.user.delegateWorkConfig,
  staffTextProfile: state.user.staffTextProfile,
  configViolationTypes: state.violationType.configViolationTypes,
  isFetchingAgencyInfo: state.agency.isFetching,
  isFetchingProductLayout: state.productLayout.isFetching,
});

const mapDispatchToProps = (dispatch: any) => ({
  logoutRequest: () => dispatch(logoutRequest()),
  fetchDelegateWorkConfig: () => dispatch(fetchDelegateWorkConfig(true)),
  fetchStaffTextProfile: (isBackgroundRefresh?: boolean) => dispatch(fetchStaffTextProfile(isBackgroundRefresh)),
  createAuditTrail: (request: IAuditTrailCreateRequest) => dispatch(createAuditTrail(request)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Header));
